// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';

type Props = {
  /**
   * When `isRunning` is `false`, the static time (in seconds) to display.
   * When `isRunning` is `true`, the starting time (in seconds) from which elapsed time is counted up.
   */
  timeInSeconds: number;
  /**
   * Whether the timer is actively running.
   * If `true`, elapsed time is continuously added to `timeInSeconds` and the display updates periodically.
   * It's assumed that `timeInSeconds` and `isRunning` are coupled/updated together.
   */
  isRunning?: boolean;
  /**
   * If true, displays time in the format HH:MM:SS. Otherwise, displays in HH:MM format.
   */
  showSeconds?: boolean;
};

/**
 * Displays a formatted duration. If `isRunning` is true, tracks and adds elapsed time to the display.
 */
export const RunningTime = ({ timeInSeconds, isRunning, showSeconds }: Props) => {
  const [totalTime, setTotalTime] = useState(timeInSeconds);

  useEffect(() => {
    // On changes to `isRunning` or `timeInSeconds, reset the timer.
    setTotalTime(timeInSeconds);
    if (!isRunning) return undefined;
    // If the timer is running, start an interval and periodically update total time.
    const timerStartedAt = Date.now();
    const interval = setInterval(() => {
      const elapsedTime = (Date.now() - timerStartedAt) / 1000;
      setTotalTime(timeInSeconds + elapsedTime);
    }, 500);
    return () => clearInterval(interval);
  }, [isRunning, timeInSeconds]);

  const [hours, minutes, seconds = 0] = convertSecondsToDuration(totalTime, showSeconds).split(':');
  const timerColonOpacity = !showSeconds && isRunning ? (Math.floor(totalTime) % 2 === 0 ? 1 : 0) : 1;
  return (
    <>
      {hours}
      <span style={{ opacity: timerColonOpacity }}>:</span>
      {minutes}
      {showSeconds && (
        <>
          <span style={{ opacity: timerColonOpacity }}>:</span>
          {seconds}
        </>
      )}
    </>
  );
};
